import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Flame, TrendingUp, Calendar, Award, XCircle, Sparkles } from 'lucide-react';
import { format } from 'date-fns';

const STREAK_MESSAGES = {
  0: 'Start your streak today.',
  1: 'Building momentum.',
  7: 'One week strong.',
  14: 'The habit is forming.',
  30: 'You are an operator.',
};

function getStreakMessage(streak) {
  if (streak >= 30) return STREAK_MESSAGES[30];
  if (streak >= 14) return STREAK_MESSAGES[14];
  if (streak >= 7) return STREAK_MESSAGES[7];
  if (streak >= 1) return STREAK_MESSAGES[1];
  return STREAK_MESSAGES[0];
}

export default function DashboardPage() {
  const [dailyLog, setDailyLog] = useState(null);
  const [stats, setStats] = useState(null);
  const [identityShifts, setIdentityShifts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [logRes, statsRes, identityRes] = await Promise.all([
        api.get('/daily/today'),
        api.get('/daily/stats'),
        api.get('/identity-shifts'),
      ]);
      setDailyLog(logRes.data);
      setStats(statsRes.data);
      setIdentityShifts(identityRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Complete onboarding to start checking in.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheck = async (itemId, currentCompleted) => {
    setChecking(itemId);
    try {
      const res = await api.post('/daily/check', {
        item_id: itemId,
        completed: !currentCompleted,
      });
      setDailyLog(res.data);

      // Refresh stats after checking
      const statsRes = await api.get('/daily/stats');
      setStats(statsRes.data);

      // Show completion banner
      if (res.data.status === 'complete') {
        setShowBanner(true);
        setTimeout(() => setShowBanner(false), 6000);
      }
    } catch (err) {
      toast.error('Failed to update. Please try again.');
    } finally {
      setChecking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-[--hc-text-muted] text-sm">Loading your day...</div>
      </div>
    );
  }

  if (!dailyLog) {
    return (
      <div className="text-center py-20">
        <p className="text-[--hc-text-muted]">No routine found. Complete onboarding first.</p>
      </div>
    );
  }

  const routineItems = dailyLog.routine_items || [];
  const operatingActions = dailyLog.operating_actions || [];
  const totalItems = routineItems.length + operatingActions.length;
  const completedItems = routineItems.filter(i => i.completed).length + operatingActions.filter(a => a.completed).length;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const currentStreak = stats?.current_streak || 0;

  const hasOldHabits = identityShifts?.old_habits?.length > 0;
  const hasNewHabits = identityShifts?.new_habits?.length > 0;
  const hasIdentityShifts = hasOldHabits || hasNewHabits;

  return (
    <div className="space-y-6">
      {/* Completion Banner */}
      {showBanner && dailyLog.status === 'complete' && (
        <div className="completion-slide-in rounded-xl bg-[--hc-accent] px-4 py-3 flex items-center justify-between" data-testid="completion-banner">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={20} className="text-[--hc-on-accent]" />
            <span className="text-sm font-bold text-[--hc-on-accent]">Operating Day Complete</span>
          </div>
          <button onClick={() => setShowBanner(false)} className="text-[--hc-on-accent]/60 hover:text-[--hc-on-accent] text-lg">&times;</button>
        </div>
      )}

      {/* Today's Status */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-sm text-[--hc-text-muted]" data-testid="dashboard-today-date">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <div className="flex items-center gap-1.5" data-testid="daily-streak-count">
              <Flame size={22} className="text-[--hc-accent]" />
              <span className="text-3xl font-bold text-[--hc-primary] tabular-nums">{currentStreak}</span>
              <span className="text-sm text-[--hc-text-muted] ml-0.5">Day Streak</span>
            </div>
          </div>
          <p className="text-sm text-[--hc-text-muted] mt-1" data-testid="streak-message">
            {getStreakMessage(currentStreak)}
          </p>
        </div>

        {/* Progress ring */}
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[--hc-surface] border border-[--hc-border]">
          <div className="relative w-12 h-12">
            <svg className="w-12 h-12 -rotate-90">
              <circle cx="24" cy="24" r="20" fill="none" stroke="var(--hc-border)" strokeWidth="4" />
              <circle
                cx="24" cy="24" r="20" fill="none"
                stroke="var(--hc-accent)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${progress * 1.257} 999`}
                className="transition-all duration-300"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[--hc-primary] tabular-nums">
              {Math.round(progress)}%
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-[--hc-text]">{completedItems}/{totalItems}</p>
            <p className="text-xs text-[--hc-text-muted]">completed</p>
          </div>
        </div>
      </div>

      {/* Non-negotiable message */}
      <div className="rounded-xl bg-[--hc-surface] border border-[--hc-border] px-4 py-3" data-testid="non-negotiable-message">
        <p className="text-xs text-[--hc-text-muted] leading-relaxed italic">
          You never skip your operating time. You are allowed to shrink it. Small action protects consistency.
        </p>
      </div>

      {/* Identity Shift Reminder */}
      {hasIdentityShifts && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="identity-shift-cards">
          {/* Old Identity */}
          {hasOldHabits && (
            <div className="rounded-xl border border-[--hc-border] bg-white overflow-hidden" data-testid="dashboard-old-identity-card">
              <div className="px-4 py-2.5 border-b border-[--hc-border] bg-[--hc-surface]/50 flex items-center gap-2">
                <XCircle size={14} className="text-[--hc-danger]/60" />
                <h3 className="text-xs font-semibold text-[--hc-text-muted] uppercase tracking-wide">Leaving Behind</h3>
              </div>
              <div className="px-4 py-3 space-y-1.5">
                {identityShifts.old_habits.map((habit, idx) => (
                  <p key={idx} className="text-xs text-[--hc-text-muted] line-through leading-relaxed">{habit}</p>
                ))}
              </div>
            </div>
          )}

          {/* New Identity */}
          {hasNewHabits && (
            <div className="rounded-xl border-2 border-[--hc-accent]/25 bg-[--hc-accent-soft]/20 overflow-hidden" data-testid="dashboard-new-identity-card">
              <div className="px-4 py-2.5 border-b border-[--hc-accent]/15 bg-[--hc-accent]/8 flex items-center gap-2">
                <Sparkles size={14} className="text-[--hc-accent]" />
                <h3 className="text-xs font-semibold text-[--hc-primary] uppercase tracking-wide">Who I'm Becoming</h3>
              </div>
              <div className="px-4 py-3 space-y-1.5">
                {identityShifts.new_habits.map((habit, idx) => (
                  <p key={idx} className="text-sm text-[--hc-text] font-medium leading-relaxed">{habit}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div data-testid="routine-progress-bar">
        <div className="w-full h-2 rounded-full bg-[--hc-surface-2] overflow-hidden">
          <div
            className="h-full rounded-full bg-[--hc-accent] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Morning Routine Checklist */}
      <div className="rounded-xl border border-[--hc-border] bg-white overflow-hidden" data-testid="routine-checklist-card">
        <div className="px-4 py-3 border-b border-[--hc-border] bg-[--hc-surface]/50">
          <h3 className="text-sm font-semibold text-[--hc-text]">Morning Routine</h3>
        </div>
        <div className="divide-y divide-[--hc-border]">
          {routineItems.map(item => (
            <button
              key={item.item_id}
              data-testid={`routine-item-toggle-${item.item_id}`}
              onClick={() => handleCheck(item.item_id, item.completed)}
              disabled={checking === item.item_id}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[--hc-surface]/30 ${
                item.completed ? 'check-flash' : ''
              }`}
            >
              {item.completed ? (
                <CheckCircle2 size={20} className="text-[--hc-accent] flex-shrink-0" />
              ) : (
                <Circle size={20} className="text-[--hc-border] flex-shrink-0" />
              )}
              <span className={`text-sm ${item.completed ? 'text-[--hc-text-muted] line-through' : 'text-[--hc-text]'}`}>
                {item.text}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Daily Operating Actions */}
      <div className="rounded-xl border border-[--hc-border] bg-white overflow-hidden" data-testid="operating-actions-card">
        <div className="px-4 py-3 border-b border-[--hc-border] bg-[--hc-surface]/50">
          <h3 className="text-sm font-semibold text-[--hc-text]">Daily Operating Actions</h3>
        </div>
        <div className="divide-y divide-[--hc-border]">
          {operatingActions.map(action => (
            <button
              key={action.key}
              data-testid={`operating-action-toggle-${action.key}`}
              onClick={() => handleCheck(action.key, action.completed)}
              disabled={checking === action.key}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[--hc-surface]/30 ${
                action.completed ? 'check-flash' : ''
              }`}
            >
              {action.completed ? (
                <CheckCircle2 size={20} className="text-[--hc-accent] flex-shrink-0" />
              ) : (
                <Circle size={20} className="text-[--hc-border] flex-shrink-0" />
              )}
              <div>
                <p className={`text-sm font-medium ${action.completed ? 'text-[--hc-text-muted] line-through' : 'text-[--hc-text]'}`}>
                  {action.title}
                </p>
                <p className="text-xs text-[--hc-text-muted] mt-0.5">{action.subtitle}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="quick-stats">
        <div className="rounded-xl border border-[--hc-border] bg-white px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-[--hc-primary]" />
            <span className="text-xs text-[--hc-text-muted]">This Week</span>
          </div>
          <p className="text-xl font-bold text-[--hc-primary] tabular-nums">
            {stats?.week_completed || 0} <span className="text-sm font-normal text-[--hc-text-muted]">/ {stats?.week_total || 7} days</span>
          </p>
        </div>
        <div className="rounded-xl border border-[--hc-border] bg-white px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={14} className="text-[--hc-primary]" />
            <span className="text-xs text-[--hc-text-muted]">This Month</span>
          </div>
          <p className="text-xl font-bold text-[--hc-primary] tabular-nums">
            {stats?.month_completed || 0} <span className="text-sm font-normal text-[--hc-text-muted]">/ {stats?.month_total || 28} days</span>
          </p>
        </div>
        <div className="rounded-xl border border-[--hc-border] bg-white px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <Award size={14} className="text-[--hc-accent]" />
            <span className="text-xs text-[--hc-text-muted]">Best Streak</span>
          </div>
          <p className="text-xl font-bold text-[--hc-primary] tabular-nums">
            {stats?.best_streak || 0} <span className="text-sm font-normal text-[--hc-text-muted]">days</span>
          </p>
        </div>
      </div>
    </div>
  );
}
